// Pieces of the partner dashboard with no JSX: the request params, the
// payout rule, the chart rows and the responsive CSS. In a `.js` file so
// `react-refresh/only-export-components` stays quiet, same as
// components/financial/formatting.js.
//
// Shape of what /partner/dashboard returns: companies/partner_dashboard_api.py
// in the backend. The route runs with `exclude_none=True`, so everything that
// only applies to a partner with a split agreement is a key that is *absent*
// for anyone else, not one that comes back null or 0. Every check here is
// therefore "is the key there", never "is it non-zero" - a real $0.00 payout
// on a quiet day and "this partner has no agreement" must not look alike.

export const DEFAULT_DASHBOARD_RANGE = { preset: 'mtd', dateFrom: null, dateTo: null }

export const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'mtd', label: 'MTD' },
  { id: 'ytd', label: 'YTD' },
]

// The balance block describes the agreement but carries no name for it, and
// the notice that does name the period is range-filtered, so it is often not
// in the response at all. There is exactly one agreement today.
export const BALANCE_TITLE = 'July 2026 settlement balance'

// The preset goes to the server as-is rather than being turned into dates
// here: resolve_range() resolves it in UTC, and the response echoes the
// resolved pair back in `range`, which is what the screen displays.
//
// Never include_test_leads: the workspace is money, and a test lead in a
// balance is money that doesn't exist. The backend ignores the flag for the
// workspace and the CSV anyway; not sending it keeps the old blocks in the
// same response consistent with them.
export function dashboardParams(range) {
  return range?.preset && range.preset !== 'custom'
    ? { range: range.preset }
    : { range: 'custom', date_from: range?.dateFrom, date_to: range?.dateTo }
}

const num = (v) => Number(v || 0)

// The four split lines arrive together or not at all (allocate() in
// companies/split_agreements.py), so `your_payout` stands for all of them.
export const hasSplit = (obj) => obj != null && obj.your_payout !== undefined

// Everything the partner is paid, never less. Under an agreement that is
// their share of the split plus whatever fell outside it (sold before the
// agreement started, paid at 100%) - `outside_split` is theirs, it is just
// not divided. Without an agreement it is the whole lead value.
export function payoutTotal(kpis) {
  if (!kpis) return 0
  if (!hasSplit(kpis)) return num(kpis.lead_value)
  return round2(num(kpis.your_payout) + num(kpis.outside_split))
}

const round2 = (n) => Math.round(n * 100) / 100

// One row per day for the stacked bars, each summing to that day's
// lead_value. The series only carries `your_payout` and `to_balance`; the
// rest of a day's value is either `outside_split` (the partner's, at 100%)
// or, once the balance has been paid off, value held for new terms. The two
// can't be told apart per day from the data, so the cut is the date the
// balance cleared: before it the remainder joins the partner's bar, from it
// onward it is its own neutral segment. Nothing is divided that the backend
// didn't divide.
export function chartRows(series, pendingBlock) {
  const since = pendingBlock?.since ? String(pendingBlock.since).slice(0, 10) : null
  return (series || []).map((point) => {
    const value = num(point.lead_value)
    if (!hasSplit(point)) {
      return { date: point.date, lead_value: value, payout: value, balance: 0, pending: 0 }
    }
    const balance = num(point.to_balance)
    const remainder = round2(value - num(point.your_payout) - balance)
    const isPending = since !== null && point.date >= since
    return {
      date: point.date,
      lead_value: value,
      payout: round2(num(point.your_payout) + (isPending ? 0 : remainder)),
      balance,
      pending: isPending ? remainder : 0,
    }
  })
}

// ── the workspace ──────────────────────────────────────────────────────────
// `workspace` in the same response (companies/partner_workspace.py). Same
// rule as above: a block the company doesn't have is an absent key, so every
// section below is rendered on presence.

const parseYmd = (ymd) => {
  const [y, m, d] = String(ymd || '').split('-').map(Number)
  return y && m && d ? { y, m, d } : null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "Oct 1 – Oct 12, 2026 · ET". Days are the backend's New York days, so the
// strings are split by hand rather than read through Date in the browser's
// own zone, which could move them by one.
export function rangeLabel(range) {
  const from = parseYmd(range?.date_from)
  const to = parseYmd(range?.date_to)
  if (!from || !to) return ''
  const day = ({ m, d }) => `${MONTHS[m - 1]} ${d}`
  if (from.y === to.y && from.m === to.m && from.d === to.d) return `${day(to)}, ${to.y} · ET`
  const start = from.y === to.y ? day(from) : `${day(from)}, ${from.y}`
  return `${start} – ${day(to)}, ${to.y} · ET`
}

export const dayLabel = (ymd) => {
  const p = parseYmd(ymd)
  return p ? `${MONTHS[p.m - 1]} ${p.d}, ${p.y}` : ''
}

export const fmtPct = (v) => `${Number(v || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
export const fmtCount = (v) => Number(v || 0).toLocaleString('en-US')

// The header line. Says only what the company has: a partner with no clicks
// campaign isn't told about clicks. Never a payout rate or revenue share -
// a partner sees what they earned, not the percentage behind it.
export function headerSubtitle(workspace) {
  const api = !!workspace?.channels?.api
  const clicks = !!workspace?.channels?.clicks
  const what = api && clicks ? 'Your leads, clicks and balance' : clicks ? 'Your clicks and balance' : 'Your leads and balance'
  return `${what} with Kickoff.`
}

// The sidebar's anchors, one per section the response actually carries.
export function workspaceSections(data) {
  const ws = data?.workspace
  const sections = [{ id: 'pw-overview', label: 'Overview', icon: 'overview' }]
  if (ws?.channels?.api) sections.push({ id: 'pw-api', label: 'API leads', icon: 'api' })
  if (ws?.channels?.clicks) sections.push({ id: 'pw-clicks', label: 'Clicks', icon: 'clicks' })
  if (ws?.purchases) sections.push({ id: 'pw-purchases', label: 'Purchases', icon: 'purchases' })
  if (ws?.net_balance || data?.balance) sections.push({ id: 'pw-statement', label: 'Statement', icon: 'statement' })
  return sections
}

export const DIRECTION_LABEL = {
  kickoff_owes_you: 'Kickoff owes you',
  you_owe_kickoff: 'You owe Kickoff',
}

// The net balance's right-hand column, each row with the sign it enters the
// total with. Rows that cannot apply (no buyer profile: nothing bought) are
// left out; a real zero stays in.
export function netBalanceRows(net) {
  if (!net) return []
  const rows = [
    { key: 'api', sign: '+', label: 'Your earnings — API leads', amount: net.api_earnings, swatch: 'var(--channel-api)' },
    { key: 'clicks', sign: '+', label: 'Your earnings — Clicks', amount: net.clicks_earnings, swatch: 'var(--channel-clicks)' },
  ]
  if (net.bought != null) rows.push({ key: 'bought', sign: '−', label: 'Leads you bought from us', amount: net.bought })
  rows.push({ key: 'paid_to_you', sign: '−', label: 'Payments sent to you', amount: net.payments_to_you })
  if (num(net.payments_from_you) > 0 || net.bought != null) {
    rows.push({ key: 'paid_by_you', sign: '+', label: 'Payments received from you', amount: net.payments_from_you })
  }
  return rows
}

// "3 min ago" / "2 h ago" / "4 days ago". `now` is injectable for tests.
export function relativeTime(iso, now = Date.now()) {
  if (!iso) return 'Never'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'Never'
  const minutes = Math.max(0, Math.round((now - then) / 60000))
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// Funnel bar widths: each step as a share of the first, so the bars read as
// "how much survives". The label beside a step is its rate against the step
// before it, which is the number a partner acts on.
export function funnelSteps(steps) {
  const first = num(steps[0]?.value)
  return steps.map((step, i) => ({
    ...step,
    width: first > 0 ? Math.max(num(step.value) / first * 100, num(step.value) > 0 ? 2 : 0) : 0,
    rate: i === 0 ? null : (num(steps[i - 1].value) > 0 ? round2(num(step.value) / num(steps[i - 1].value) * 100) : 0),
  }))
}

// The stacked daily bars: API at the bottom, clicks on top.
export const earningsRows = (daily) => (daily || []).map((d) => ({
  date: d.date, api: num(d.api_earnings), clicks: num(d.clicks_earnings),
}))

export const PARTNER_DASHBOARD_RESPONSIVE_CSS = `
/* Sin comillas invertidas en estos comentarios: esto vive dentro de un
   template literal de JS y una comilla invertida lo cerraria. */
.pd-kpis {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(var(--pd-kpi-count, 4), minmax(0, 1fr));
}
.pd-skeleton {
  background: var(--bg-raised);
  border-radius: 6px;
  animation: pd-pulse 1.2s ease-in-out infinite;
}
@keyframes pd-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
@media (prefers-reduced-motion: reduce) { .pd-skeleton { animation: none; } }
.pd-quality > summary {
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  user-select: none;
}
.pd-quality > summary::-webkit-details-marker { display: none; }
.pd-quality > summary:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 6px; }
.pd-quality .pd-chevron { flex-shrink: 0; transition: transform 120ms ease; color: var(--text-muted); }
.pd-quality[open] .pd-chevron { transform: rotate(90deg); }
.pd-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
@media (max-width: 1200px) {
  .pd-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
}
@media (max-width: 900px) {
  .pd-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  .pd-split { flex-direction: column !important; align-items: stretch !important; }
}
@media (max-width: 768px) {
  .pd-header { flex-direction: column !important; align-items: stretch !important; }
  .pd-range { justify-content: flex-start !important; }
  .pd-range-custom { width: 100%; }
  .pd-range-custom input { flex: 1; min-width: 0; font-size: 16px !important; }
  .pd-section-head { flex-direction: column !important; align-items: flex-start !important; }
}
@media (max-width: 520px) {
  .pd-kpis { grid-template-columns: 1fr !important; }
}

/* The workspace: white sidebar on the left, content on the right. Below
   1100px the sidebar becomes a row on top; below 900px every grid is one
   column. */
.pw-layout {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}
.pw-sidebar {
  position: sticky;
  top: 0;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.pw-nav { display: flex; flex-direction: column; gap: 2px; }
.pw-nav a, .pw-nav button {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 10px; border: 0; background: none;
  color: var(--text-secondary); font: inherit; font-size: 13px; font-weight: 600;
  text-decoration: none; cursor: pointer; white-space: nowrap; text-align: left;
}
.pw-nav a:hover, .pw-nav button:hover { background: var(--bg-raised); color: var(--text-primary); }
.pw-nav a:focus-visible, .pw-nav button:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }
.pw-main { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
.pw-main section { scroll-margin-top: 12px; }
.pw-card {
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 20px;
  min-width: 0;
}
.pw-title { font-family: var(--font-display); margin: 0; color: var(--text-primary); }
.pw-num { font-variant-numeric: tabular-nums; }
.pw-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
.pw-header { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.pw-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
.pw-net {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
  gap: 28px;
  background: var(--kpi-hero-bg);
  color: var(--kpi-hero-fg);
  border-radius: var(--radius-lg);
  padding: 24px;
}
.pw-channels { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.pw-bottom { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: 16px; align-items: start; }
.pw-minikpis { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.pw-kpis4 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; }
.pw-link-box { display: flex; align-items: center; gap: 8px; min-width: 0; }
.pw-link-box code { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
@media (max-width: 1100px) {
  .pw-layout { grid-template-columns: minmax(0, 1fr); }
  .pw-sidebar { position: static; flex-direction: row; align-items: center; flex-wrap: wrap; padding: 10px 12px; gap: 10px; }
  .pw-nav { flex-direction: row; overflow-x: auto; flex: 1; min-width: 0; }
  .pw-sidebar-extra { display: none !important; }
}
@media (max-width: 900px) {
  .pw-channels, .pw-bottom, .pw-net { grid-template-columns: minmax(0, 1fr); }
  .pw-header .pd-range { flex-wrap: wrap; }
  .pw-header { align-items: stretch; }
  .pw-header-actions { justify-content: flex-start; }
}
@media (max-width: 560px) {
  .pw-kpis4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pw-card, .pw-net { padding: 16px; }
}
@media (max-width: 360px) {
  .pw-minikpis { grid-template-columns: minmax(0, 1fr); }
}
`
